import { useState, type ReactNode } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ReactComponent as RightArrowIcon } from '../../../../assets/icons/arrow-right.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/xerror.svg';
import { CLIENTS_ROUTE } from '../../../constants/auth';
import {
  ACTIVATION_MIN_LEAD_DAYS_APP_ONLY,
  ACTIVATION_MIN_LEAD_DAYS_APP_ONLY_WITH_DOCS,
  ACTIVATION_MIN_LEAD_DAYS_WITH_INSURER
} from '../../../constants/prospects';
import { CANCEL_ACTIVATION, CLICK_ACTION, SUBMIT_ACTIVATION } from '../../../constants/tagManager';
import { useBroker } from '../../../hooks/useBroker/useBroker';
import { useUser } from '../../../hooks/useUser/useUser';
import type { IProspect } from '../../../interfaces/prospects';
import type { IQuote } from '../../../interfaces/quotes';
import Prospects from '../../../services/api/prospects/prospects';
import tagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import { ClientsStatus } from '../../../types/clients';
import {
  addDays,
  formatDateDisplay,
  formatDateISO,
  getFirstOfNextMonth,
  parseDateISO
} from '../../../utils/dates/dates';
import { isAppOnly } from '../../../utils/prospects/prospects';
import { quoteHasExternalBenefits } from '../../../utils/quotes/quotes';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import BannerFill from '../../Globals/BannerFill/BannerFill';
import Tooltip from '../../Globals/Tooltip/Tooltip';
import CustomInput from '../../Inputs/CustomInput/CustomInput';
import { CustomModal } from '../CustomModal/CustomModal';

import './ProspectActivationModal.scss';

export interface ProspectActivationModalProps {
  open: boolean;
  prospect: IProspect | null;
  quoteIds: number[];
  quotes?: IQuote[];
  onClose: () => void;
  onSuccess?: () => void;
  title?: ReactNode;
  intro?: ReactNode;
}

export default function ProspectActivationModal({
  open,
  prospect,
  quoteIds,
  quotes = [],
  onClose,
  onSuccess,
  title,
  intro
}: Readonly<ProspectActivationModalProps>) {
  const { brokerId } = useUser();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const MODAL = TRANSLATIONS_PROSPECTS.profile.activationSetup.activationModal;

  const { commercialAddons } = useBroker();

  const appOnly = isAppOnly(prospect);
  const hasExternalBenefits = quotes.some(quoteHasExternalBenefits);

  const enrollmentWindowDays =
    commercialAddons
      .flatMap((typeGroup) => typeGroup.groups)
      .map((entry) => entry.group.choiceWindowDays)
      .find((days): days is number => days != null) ?? 0;
  const appOnlyMinLeadDays = hasExternalBenefits
    ? ACTIVATION_MIN_LEAD_DAYS_APP_ONLY_WITH_DOCS
    : ACTIVATION_MIN_LEAD_DAYS_APP_ONLY;
  const minLeadDays = appOnly ? appOnlyMinLeadDays : ACTIVATION_MIN_LEAD_DAYS_WITH_INSURER;
  const minDateISO = formatDateISO(addDays(new Date(), minLeadDays));
  const [enrollStart, setEnrollStart] = useState(minDateISO);
  const [failed, setFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setFailed(false);
      setSubmitting(false);
      setEnrollStart(minDateISO);
    }
  }

  const handleConfirm = async () => {
    if (!prospect || !brokerId) return;

    setFailed(false);
    setSubmitting(true);
    try {
      await Prospects.activateProspect(brokerId, prospect.id, enrollStart, quoteIds);
      tagManagerService.pushEvent(CLICK_ACTION, SUBMIT_ACTIVATION);
      onClose();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(CLIENTS_ROUTE, { state: { status: ClientsStatus.AwaitingStart } });
      }
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleCancel = () => {
    tagManagerService.pushEvent(CLICK_ACTION, CANCEL_ACTIVATION);
    onClose();
  };

  const renderSummary = () => {
    const enrollStartDate = parseDateISO(enrollStart);
    const enrollEndDate = addDays(enrollStartDate, enrollmentWindowDays);
    const policyStartDate = getFirstOfNextMonth(enrollEndDate);

    return (
      <div className="activation-modal__summary">
        <div className="activation-modal__summary-title">{t(MODAL.summaryTitle)}</div>

        <div className="activation-modal__rows">
          <div className="activation-modal__row">
            <span className="activation-modal__row-label">{t(MODAL.enrollmentOpens)}</span>
            <span className="activation-modal__row-value">{formatDateDisplay(enrollStartDate, i18n.language)}</span>
          </div>
          <div className="activation-modal__row">
            <span className="activation-modal__row-label">{t(MODAL.enrollmentCloses)}</span>
            <span className="activation-modal__row-value">{formatDateDisplay(enrollEndDate, i18n.language)}</span>
          </div>
        </div>

        <div className="activation-modal__policy">
          <span className="activation-modal__policy-label">{t(MODAL.policyEffective)}</span>
          <span className="activation-modal__policy-value">{formatDateDisplay(policyStartDate, i18n.language)}</span>
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={title ?? t(appOnly ? MODAL.titleAppOnly : MODAL.title)}
      size="l"
    >
      <div className="activation-modal">
        {failed && (
          <div className="activation-modal__error">
            <BannerFill
              variant="danger"
              inline
              icon={<ErrorIcon aria-hidden="true" />}
              title={t(MODAL.error)}
              screenReaderLabel={t(MODAL.errorLabel)}
            />
          </div>
        )}
        <p className="activation-modal__intro">
          {intro ?? (
            <Trans
              i18nKey={appOnly ? MODAL.introAppOnly : MODAL.intro}
              values={{ name: prospect?.companyName ?? '' }}
              components={{ b: <strong /> }}
            />
          )}
        </p>

        <div className="activation-modal__field">
          <span className="activation-modal__label">
            <span>{t(appOnly ? MODAL.startDateLabel : MODAL.enrollmentLabel)}</span>
            {!appOnly && (
              <Tooltip
                content={t(MODAL.enrollmentTooltip)}
                placement="top"
                inline
                triggerClassName="activation-modal__tooltip-trigger"
              >
                <InfoIcon className="activation-modal__info-icon" aria-label={t(MODAL.enrollmentTooltip)} />
              </Tooltip>
            )}
          </span>
          <CustomInput
            type="date"
            value={enrollStart}
            min={minDateISO}
            onChange={(e) => setEnrollStart(e.target.value)}
          />
        </div>

        {appOnly && !hasExternalBenefits && <p className="activation-modal__benefits-note">{t(MODAL.benefitsNote)}</p>}

        {!appOnly && renderSummary()}

        <div className="activation-modal__footer">
          <CustomButton variant="default" size="m" onClick={handleCancel} disabled={submitting}>
            {t(MODAL.cancel)}
          </CustomButton>
          <CustomButton variant="primary" size="m" onClick={handleConfirm} loading={submitting}>
            {t(MODAL.confirm)}
            <RightArrowIcon className="activation-modal__arrow" />
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
}
